// Copyright ©️ 2026 eVolve MEP, LLC

import type { SelectableCategory, GlobalAggregatedBOMItem } from '@evolvemep/api-ts';
import type { ColDef } from 'ag-grid-enterprise';

import { EMPTY_GUID } from 'constants/globalConstants';
import { customSetFilterParams } from 'helpers/ag-grid/convertAgGridRequest';
import { CustomValueSetFilterComponent } from 'helpers/ag-grid/CustomSetFilterComponent';
import { destructureAndFormatQuantity } from 'helpers/formatQuantity';
import type { AggregatedBOMTableAgContext } from 'modules/Shop/BillOfMaterials/types';

export const getBillOfMaterialsColumnDefs = (categories: SelectableCategory[]): ColDef<GlobalAggregatedBOMItem>[] => [
  {
    field: 'item',
    headerName: 'Item',
    minWidth: 200,
    flex: 1,
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['reset'],
    },
  },
  {
    field: 'quantity',
    headerName: 'Item Qty',
    minWidth: 80,
    valueFormatter: destructureAndFormatQuantity,
  },
  {
    field: 'unitOfMeasure',
    headerName: 'UOM',
    minWidth: 100,
    filter: 'agSetColumnFilter',
    filterParams: {
      keyCreator: ({ value }: { value: string[] }) => value,
      buttons: ['apply', 'reset'],
      values: ({ context, success }: { context: AggregatedBOMTableAgContext; success: (options: string[]) => void }) =>
        success(context.uoms?.map((m) => m.code) ?? []),
    },
  },
  {
    field: 'category',
    filter: CustomValueSetFilterComponent(
      customSetFilterParams(
        [
          {
            category: '(No category)',
            categoryId: EMPTY_GUID,
            catalog: null,
          },
          ...categories,
        ],
        'categoryId',
        'category',
        { filterKey: 'categoryIds', labelDescriptor: (c) => c.catalog },
      ),
    ),
  },
  {
    field: 'manufacturer',
    headerName: 'Manufacturer',
    minWidth: 120,
    filter: 'agSetColumnFilter',
    filterParams: {
      keyCreator: ({ value }: { value: string[] }) => value,
      buttons: ['apply', 'reset'],
      values: ({ context, success }: { context: AggregatedBOMTableAgContext; success: (options: string[]) => void }) =>
        success(context.manufacturers?.map((m) => m.name) ?? []),
    },
  },
  {
    field: 'catalogNumber',
    headerName: 'Catalog Number',
    minWidth: 150,
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['reset'],
    },
  },
];
